import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { InternalFieldName } from 'react-hook-form/dist/types/fields';
import Form from '../Crud/Form';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import { useQuery } from '../../services/utils';
import CustomSelect from '../CustomSelect/CustomSelect';

export interface InvoiceFormFields {
  customer: number;
  company: number;
  transactionNature: number;
}

interface InvoiceFormProps {
  isUpdate?: boolean;
  isLoading: boolean;
  title: string;
  formFields: InvoiceFormFields;
  formErrors: Record<string, any>;
  onSubmit: (data: any) => void;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function InvoiceForm({
  isUpdate = false,
  isLoading,
  title,
  formFields,
  formErrors,
  onSubmit,
}: InvoiceFormProps) {
  const {
    control,
    setValue,
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<InvoiceFormFields>({ mode: 'onBlur' });

  const {
    user: { organization },
  } = useAuth();
  const query = useQuery();

  useEffect(() => {
    reset(formFields);
  }, [formFields]);

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transactionNatures, setTransactionNatures] = useState([]);

  async function getCompanies() {
    if (organization) {
      const response = await api.get(
        `organizations/${organization.id}/companies`
      );

      setCompanies(response.data.data);
    }
  }

  async function getCustomers() {
    if (organization) {
      const response = await api.get(
        `organizations/${organization.id}/customers`
      );
      setCustomers(response.data.data);

      const customerId = query.get('customerId');
      if (customerId) {
        reset({ customer: Number(customerId) });
      }
    }
  }

  async function getTransactionNatures() {
    if (organization) {
      const response = await api.get(
        `organizations/${organization.id}/transaction-natures`
      );
      setTransactionNatures(response.data.data);
    }
  }

  useEffect(() => {
    getCompanies();
    getCustomers();
    getTransactionNatures();
  }, [organization]);

  return (
    <Form isLoading={isLoading} title={title} onSubmitCallback={() => {}}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label'>
            Empresa
          </label>
          <select
            required
            className='form-select'
            aria-label='Selecione a Empresa'
            {...register('company', { required: true })}
          >
            <option value=''>Selecione a Empresa</option>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {errors.company && (
            <div className='form-text text-danger'>
              {errors.company.message}
            </div>
          )}
        </div>

        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className='form-label'>Cliente</label>
          <Controller
            name='customer'
            control={control}
            render={({ field }) => (
              <CustomSelect
                options={customers.map((customer: any) => ({
                  label: customer.name,
                  value: customer.id,
                }))}
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder='Selecione o Cliente'
              />
            )}
          />
        </div>

        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label'>
            Natureza da Transação
          </label>
          <select
            required
            className='form-select'
            aria-label='Selecione a Natureza da Transação'
            {...register('transactionNature', { required: true })}
          >
            <option value=''>Selecione a Natureza da Transação</option>
            {transactionNatures.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {errors.transactionNature && (
            <div className='form-text text-danger'>
              {errors.transactionNature.message}
            </div>
          )}
        </div>

        <div className='d-flex justify-content-end gap-2'>
          <Link to='/invoices' className='btn btn-secondary'>
            Cancelar
          </Link>
          <button type='submit' className='btn btn-primary'>
            Salvar
          </button>
        </div>
      </form>
    </Form>
  );
}
